import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { PershingTranslationService } from '@pershing/pershing-core';
import { BalancesResponse, CustomGridHeader } from '../../model/index';
import { BalancesDetailsChildrowsComponent } from './balances-details-childrows.component';
import { CellRenderKeyComponent } from '../supporting/cellRender-key.component';
import { BalancesDetailsCombinedCellRenderComponent } from './balances-details-combined-cellRender.component';
import { NxiBalancesGridHeaderComponent } from '../supporting/nxi-balances-grid-header.component';
import { BalancesLabels } from '../../i18n/balances-labels';
import { BalancesService } from '../../services/balances.services';
import { ClientSideRowModelModule, GridOptions, Module } from '@ag-grid-community/all-modules';
import { RowGroupingModule } from '@ag-grid-enterprise/all-modules';
import { BalancesExportUtil } from '../../utils/balances-export-util';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'balances-details-combined-grid',
    templateUrl: './balances-details-combined-grid.component.html'
})
export class BalancesDetailsCombinedGridComponent implements OnInit, OnDestroy {

    @Input() set balancesResponse(balancesResponse: BalancesResponse) {
        if (balancesResponse) {
            this.createBalanceRowData(balancesResponse);
        }
    }

    hoverTooltip: string = 'Detail - Account Balances';
    @Input() rowData: any[];
    gridOptions: GridOptions;
    public modules: Module[] = [ClientSideRowModelModule, RowGroupingModule];
    customHeaders: any;
    columnDefs: any[] = [
        {
            headerName: BalancesLabels.DETAIL_LABELS_PERSHINGLLC,
            field: 'value',
            headerClass: 'ag-right-aligned-header ag-cell-label-container bal-BalancesDetails_Custodian1 padding-top-0 margin-left-10',
            cellClass: 'right-align bal-BalancesDetails_Custodian1',
            cellRendererFramework: BalancesDetailsCombinedCellRenderComponent
        },
        {
            headerName: BalancesLabels.DETAIL_LABELS_BNYMELLONNA,
            field: 'value',
            headerClass: 'ag-right-aligned-header ag-cell-label-container bal-BalancesDetails_Custodian2 padding-top-0 margin-left-10',
            cellClass: 'right-align bal-BalancesDetails_Custodian2',
            cellRendererFramework: BalancesDetailsCombinedCellRenderComponent
        }
    ];
    isLoadingBalanceData: boolean = true; // subsequent load when changing acc
    unsubscribe: Subject<void> = new Subject<void>();

    constructor(
        private translationService: PershingTranslationService,
        private balancesService: BalancesService
    ) { }

    initGrid() {
        this.initCustomHeaders();
        this.gridOptions = {
            suppressContextMenu: true,
            columnDefs: this.columnDefs,
            suppressMovableColumns: true,
            autoGroupColumnDef: { 
                headerName: '',
                cellRendererParams: { innerRendererFramework: CellRenderKeyComponent, suppressCount: true }
            },
            overlayLoadingTemplate: '<div class="progress-spinner"></div>',
            domLayout: 'autoHeight',
            onGridSizeChanged: () => {
                if (this.gridOptions.api) { this.gridOptions.api.sizeColumnsToFit(); }
            },
            onGridReady: ($event) => {
                this.gridOptions.api.sizeColumnsToFit();
            },
            headerHeight: 70,
            rowHeight: 50,
            treeData: true,
            getDataPath: function (data){
                return data.heirarchy;
            }
        };
        this.gridOptions.defaultColDef = {
            headerComponentFramework: NxiBalancesGridHeaderComponent,
            headerComponentParams: this.customHeaders,
           // tooltip: (params) => this.hoverTooltip
        };
    }

    doesDataFlower(data: any) {
        return (data && data.children && data.children.length > 0);
    }

    getFullWidthCellRenderer() {
        return BalancesDetailsChildrowsComponent;
    }

    isFullWidthCell(rowNode: any) {
        return rowNode.level === 1;
    }

    ngOnInit() {
        this.translationService.addTranslationFolder('@pershing/nxi-ng-balances', 'node_modules/@pershing/nxi-ng-balances/lib');
        this.initGrid();
        this.balancesService.fetchingBalancesData$.pipe(takeUntil(this.unsubscribe)).subscribe(fetching => {
            this.isLoadingBalanceData = !!fetching ? fetching : false;
        });
    }

    createBalanceRowData(rsp: BalancesResponse) {
        if (!rsp || rsp.response.balancesList[0].balancesSummary === null || rsp.response.balancesList[0].balancesDetails === null) {
            return;
        }
        let bd = rsp.response.balancesList[0].balancesDetails;
        let bs = rsp.response.balancesList[0].balancesSummary;
        let bdc = rsp.response.balancesList[1].balancesDetails;
        let bsc = rsp.response.balancesList[1].balancesSummary;
        this.rowData = BalancesExportUtil.processBalancesDetails(bd, bdc, bs, bsc, "combined", "desktop", this.balancesService);
    }

    initCustomHeaders() {
        let customHeaders = {};
        let acctTypeHeader: CustomGridHeader[] = [];
        acctTypeHeader.push(
            {
                'name': BalancesLabels.DETAIL_LABELS_BNYMELLONNA,
                'footer': '',
                'className': 'accountType subHeadingDisplay'
            }
        );
        acctTypeHeader.push(
            {
                'name': BalancesLabels.DETAIL_LABELS_BALANCESTODAYCHNG,
                'footer': '',
                'className': 'todaysChange subHeadingDisplay'
            }
        );
        let acctTypeHeaderBrokerage: CustomGridHeader[] = [];
        acctTypeHeaderBrokerage.push(
            {
                'name': BalancesLabels.DETAIL_LABELS_PERSHINGLLC,
                'footer': '',
                'className': 'accountType subHeadingDisplay'
            }
        );
        acctTypeHeaderBrokerage.push(
            {
                'name': BalancesLabels.DETAIL_LABELS_BALANCESTODAYCHNG,
                'footer': '',
                'className': 'todaysChange subHeadingDisplay'
            }
        );
        customHeaders[BalancesLabels.DETAIL_LABELS_BNYMELLONNA] = acctTypeHeader;
        customHeaders[BalancesLabels.DETAIL_LABELS_PERSHINGLLC] = acctTypeHeaderBrokerage;
        this.customHeaders = { 'customHeaders': customHeaders };
    }

    ngOnDestroy(): void {
        this.balancesService.updateDisclaimer({
            'legendSymbol': '', legendByIndex: true, 'key': BalancesLabels.DISCLAIMER_LONGMARKETVALUE,
            'show': false, 'className': 'longMarketValue', 'showCounter': 0
        });
        this.balancesService.updateDisclaimer({
            'legendSymbol': '', 'legendByIndex': true, 'key': BalancesLabels.DISCLAIMER_SECURITYOWED,
            'show': false, 'className': 'securityOwed', 'showCounter': 0
        });
        this.balancesService.updateDisclaimer({
            'legendSymbol': '', 'legendByIndex': true, 'key': BalancesLabels.DISCLAIMER_CASHMGTINT,
            'show': false, 'className': 'cashMgtInt', 'showCounter': 0
        });
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
